import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 100px 20px 40px; /* Increased top padding to 100px */
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-light);
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.1);
    transform: translateX(-5px);
  }
  
  svg {
    font-size: 14px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 30px;
  color: #fff;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 10px;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 15px;
  color: var(--primary-light);
`;

const Paragraph = styled.p`
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 15px;
  color: rgba(255, 255, 255, 0.8);
`;

const List = styled.ul`
  margin-left: 20px;
  margin-bottom: 15px;
`;

const ListItem = styled.li`
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
`;

const ContactInfo = styled.div`
  background-color: rgba(59, 130, 246, 0.1);
  border-radius: 8px;
  padding: 15px;
  border: 1px solid rgba(59, 130, 246, 0.2);
  margin-top: 20px;
`;

const EmailLink = styled.a`
  color: var(--primary-light);
  text-decoration: underline;
  
  &:hover {
    color: var(--primary-color);
  }
`;

const Privacy = () => {
  const navigate = useNavigate();
  
  const handleBack = () => {
    navigate(-1);
  };
  
  return (
    <PageContainer>
      <BackButton onClick={handleBack}>
        <FontAwesomeIcon icon={faArrowLeft} />
        Back
      </BackButton>
      
      <Title>Privacy Policy</Title>
      
      <Section>
        <Paragraph>
          Welcome to Odelu.fun. We respect your privacy and are committed to protecting your personal information.
          This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Information We Collect</SectionTitle>
        <Paragraph>
          We may collect the following types of information when you use our service:
        </Paragraph>
        <List>
          <ListItem>
            <strong>Personal Information:</strong> Email address (only if you choose to provide it for account creation)
          </ListItem>
          <ListItem>
            <strong>Usage Data:</strong> Information on how you access and use our service, including your IP address, browser type, pages visited, and time spent
          </ListItem>
          <ListItem>
            <strong>Cookies and Similar Technologies:</strong> We use cookies to enhance your browsing experience and analyze usage patterns
          </ListItem>
        </List>
      </Section>
      
      <Section>
        <SectionTitle>How We Use Your Information</SectionTitle>
        <Paragraph>
          We use the collected information for the following purposes:
        </Paragraph>
        <List>
          <ListItem>To provide and maintain our service</ListItem>
          <ListItem>To notify you about changes to our service</ListItem>
          <ListItem>To allow you to participate in interactive features when you choose to do so</ListItem>
          <ListItem>To provide customer support</ListItem>
          <ListItem>To gather analysis or valuable information to improve our service</ListItem>
          <ListItem>To monitor the usage of our service</ListItem>
          <ListItem>To detect, prevent and address technical issues</ListItem>
        </List>
      </Section>
      
      <Section>
        <SectionTitle>Content Disclaimer</SectionTitle>
        <Paragraph>
          <strong>Important:</strong> Odelu.fun does not host any content on our servers. We function solely as a frontend interface to content that is hosted elsewhere on the internet. We do not have control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party sites or services.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Data Security</SectionTitle>
        <Paragraph>
          The security of your data is important to us, but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Changes to This Privacy Policy</SectionTitle>
        <Paragraph>
          We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "effective date" at the top of this page.
        </Paragraph>
        <Paragraph>
          You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Contact Us</SectionTitle>
        <Paragraph>
          If you have any questions about this Privacy Policy, please contact us:
        </Paragraph>
        <ContactInfo>
          <Paragraph>
            By email: <EmailLink href="mailto:odelu0@proton.me">odelu0@proton.me</EmailLink>
          </Paragraph>
          <Paragraph>
            Website: <EmailLink href="https://odelu.fun" target="_blank" rel="noopener noreferrer">odelu.fun</EmailLink>
          </Paragraph>
        </ContactInfo>
      </Section>
    </PageContainer>
  );
};

export default Privacy; 